<template>
  <v-container fluid fill-height>
    <v-layout align-center justify-center>
      <v-flex xs12 sm8 md4>
        <v-card class="elevation-12">
          <v-toolbar dark color="primary">
            <v-toolbar-title>{{ $vuetify.t('$vuetify.Login.welcome') }}</v-toolbar-title>
            <v-spacer></v-spacer>
          </v-toolbar>
          <v-card-text>
            <v-form id="login"  @submit="login">
              <v-autocomplete
                v-if="!hideSchools"
                name='school'
                prepend-icon="school"
                :items="schools"
                :label="$vuetify.t('$vuetify.Login.school')"
                @change="handleChangeSchool"
                v-validate="'required'"
                v-model="selectedSchoolId"
                :error-messages="errors.collect('school')"
              ></v-autocomplete>
              <v-text-field
                autocapitalize="none"
                name='username'
                prepend-icon="person"
                v-model="username"
                :label="$vuetify.t('$vuetify.Login.login')"
                v-validate="'required'"
                :error-messages="errors.collect('username')"
                type="text">
              </v-text-field>
              <v-text-field
                name='password'
                id="password"
                prepend-icon="lock"
                v-model="password"
                :label="$vuetify.t('$vuetify.Login.password')"
                v-validate="'required'"
                @keyup.native.enter="login"
                :error-messages="errors.collect('password')"
                :type="passwordFieldType">
              </v-text-field>
              <v-checkbox
                class="password-visibility-checkbox"
                @change="changePasswordVisibility()"
                :label="$vuetify.t('$vuetify.Login.showPassword')"
              ></v-checkbox>
              <v-expand-transition>
                <div v-show="showCustomServer">
                  <div style="display: flex">
                    <v-text-field
                      autocapitalize="none"
                      name='server'
                      id="server"
                      :color="serverColor"
                      prepend-icon="storage"
                      v-model="server"
                      :label="$vuetify.t('$vuetify.Login.currentServer')"
                      :hint="$vuetify.t('$vuetify.Login.updateServer')"
                      @keyup.native.enter="updateServer"
                      @focus="editServer = true"
                      @blur="confirmServer"
                      :error-messages="errors.collect('server')"
                      type="text">
                    </v-text-field>
                    <v-slide-x-reverse-transition>
                      <v-btn
                        dark
                        :color="serverColor"
                        @click.stop="updateServer">
                        {{ $vuetify.t('$vuetify.Login.update') }}
                      </v-btn>
                    </v-slide-x-reverse-transition>
                  </div>
                  <v-expand-transition>
                    <div v-show="requireUpdate">
                      <v-alert
                        type="warning"
                        color="red"
                        class="dense"
                        v-show="alertText !== ''"
                      >
                        {{ alertText }}
                      </v-alert>
                    </div>
                  </v-expand-transition>
                </div>
              </v-expand-transition>
            </v-form>
          </v-card-text>
          <v-card-actions class="px-4 pt-0">
            <v-switch
              v-model="showCustomServer"
              class="ma-2"
              :label="$vuetify.t('$vuetify.Login.server')"
              >
            </v-switch>
            <v-spacer></v-spacer>
            <v-btn
              dark
              type="submit"
              color="green darken-1"
              form="check-login-form"
              @click.stop="login">
              {{ $vuetify.t('$vuetify.Login.button') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-flex>
    </v-layout>
    <v-snackbar
      v-model="showSnackbar"
      :bottom="true"
    >
      {{ snackbarMsg }}
      <v-btn
        color="pink"
        flat
        @click="showSnackbar = false"
      >
        {{ $vuetify.t('$vuetify.Snackbar.close') }}
      </v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>
import api from '@/api'
import service from '@/api/service'
import { getLoginData, getFetchData } from '@/utils/auth.js'

export default {
  $_veeValidate: { validator: 'new' },

  name: 'Login',
  data: () => ({
    username: '',
    password: '',
    server: '',
    schools: [],
    selectedSchoolId: null,
    hideSchools: true,
    showSnackbar: false,
    snackbarMsg: '',
    showCustomServer: false,
    editServer: false,
    requireUpdate: false,
    alertText: '',
    passwordFieldType: 'password'
  }),

  beforeMount: function () {
    service.defaults.baseURL = this.$store.getters.activeServer
    this.server = this.$store.getters.customServer
    this.showCustomServer = this.$store.getters.useCustomServer
    this.loadSchools()
  },

  props: {
    source: String
  },

  computed: {
    serverColor: function () {
      return this.requireUpdate ? 'red' : 'green darken-1'
    }
  },

  methods: {
    login: function () {
      this.$validator.validate()
        .then(isFormValid => {
          if (!isFormValid) {
            this.showSnackbar = true
            this.snackbarMsg = this.$vuetify.t('$vuetify.Snackbar.formError')
            return
          }

          const params = {
            data: {
              username: this.username,
              password: this.password,
              school_id: this.selectedSchoolId
            },
            redirect: { name: 'IdeaSpaces' },
            fetchUser: true
          }
          this.$auth.login(params)
            .then((res) => {
              this.$store.commit('SET_USER', res.data.data)
              this.$auth.user(res.data.data)
              this.$store.commit('SET_SELECTED_SCHOOL', res.data.data.school_id)
              if (window.hasOwnProperty('cordova')) {
                api.user.updateUserConfig(res.data.data.user_id, 'firebase_id', this.$store.getters.firebase_id)
              }
              api.school.getConfig(res.data.data.school_id).then(res => {
                this.$store.commit('SET_SCHOOL_CONFIG', res.data)
              })
            })
            .catch((error) => {
              if (error.response && error.response.status === 401) {
                this.showSnackbar = true
                this.snackbarMsg = this.$vuetify.t(
                  '$vuetify.Login.passwordError'
                )
              } else {
                this.showSnackbar = true
                this.snackbarMsg = this.$vuetify.t(
                  '$vuetify.Snackbar.serverError'
                )
              }
            })
        })
    },
    loadSchools: function () {
      api.school.getPublic()
        .then(res => {
          this.schools = res.data
          let hostnameParts = window.location.hostname.split('.')

          if (hostnameParts[0] !== 'germany') {
            let schoolIdx = this.schools.findIndex(s => s.domain === hostnameParts[0])
            if (schoolIdx >= 0) {
              this.hideSchools = true
              this.selectedSchoolId = this.schools[schoolIdx].value
            } else {
              this.hideSchools = false
            }
          } else {
            this.hideSchools = false
          }
        })
        .catch((error) => {
          this.schools = []
          if (error.response && error.response.status === 404) {
            this.requireUpdate = true
            this.alertText = this.$vuetify.t(
              '$vuetify.Login.serverAlert'
            )
          }
        })
    },
    handleChangeSchool: function (value) {
      this.selectedSchoolId = value
    },
    confirmServer: function () {
      if (this.server === this.$store.getters.customServer && this.schools.length > 0) {
        this.editServer = false
        this.requireUpdate = false
      } else {
        this.requireUpdate = true
        this.alertText = this.$vuetify.t(
          '$vuetify.Login.updateAlert'
        )
      }
    },
    updateServer: function () {
      if (this.server === '') {
        this.showCustomServer = false
        this.server = this.$store.getters.customServer
      } else {
        this.$store.commit('SET_SERVER', this.server)
      }
      service.defaults.baseURL = this.$store.getters.activeServer
      this.$auth.options.loginData = getLoginData(this.$store.getters.activeServer)
      this.$auth.options.fetchData = getFetchData(this.$store.getters.activeServer)
      this.loadSchools()
      this.editServer = false
      this.requireUpdate = false
    },
    changePasswordVisibility: function () {
      if (this.passwordFieldType === 'password') {
        this.passwordFieldType = 'text'
      } else {
        this.passwordFieldType = 'password'
      }
    }
  },

  watch: {
    showCustomServer: function (value) {
      this.$store.commit('TOGGLE_SERVER', value)
      if (this.$store.getters.activeServer !== '') {
        service.defaults.baseURL = this.$store.getters.activeServer
        this.$auth.options.loginData = getLoginData(this.$store.getters.activeServer)
        this.$auth.options.fetchData = getFetchData(this.$store.getters.activeServer)
        this.loadSchools()
      }
    },
    requireUpdate: function (value) {
      if (value === false) {
        this.alertText = ''
      }
    }
  }
}
</script>

<style scoped>
  .dense {
    padding-top: 8px;
    padding-bottom: 8px;
    margin-top: 0;
    border: none;
    border-radius: 2px;
  }
  .password-visibility-checkbox {
    margin-left: 32px;
    margin-top: -12px;
  }
</style>
